// Tells a logged-in customer that a cart change is moving their discounts: a
// toast that reads "recalculating" until personal-price.js has painted the new
// prices, then "applied" before it fades out. Meanwhile every price block that
// can move (the parent of a .son-personal-price node) shows a loader. Guests
// get neither: their prices cannot move, and they get no sonPersonalPrice data.
//
// Toast and loader wait SHOW_DELAY first: an answer that comes sooner shows no
// "recalculating" at all. "Applied" only follows when a number actually changed
// (son-price-changed from price-roll.js); otherwise the toast just fades.
//
// A cycle starts on Woo's adding_to_cart, or on start() from my-deals.js, whose
// cart changes Woo never announces until they are done. It may only end on the
// son-personal-price-settled that follows the cart change itself: an earlier
// refresh, such as the one on page load, can settle in between.
// Styling lives in scss/_discount-toast.scss.

const SHOW_DELAY = 300;
const MIN_LOADING = 700;
const APPLIED_SHOWN = 2000;
// a failed add announces nothing, and the toast must not hang on
const MAX_WAIT = 15000;

const config = () => window.sonPersonalPrice;

let phase = 'idle'; // idle | cart | prices
let shownAt = 0;
let shown = false;
let changed = false;
let toast = null;
let timers = [];

function clearTimers() {
    timers.forEach(clearTimeout);
    timers = [];
}

function later(fn, delay) {
    timers.push(setTimeout(fn, delay));
}

function element() {
    if (!toast) {
        toast = document.createElement('div');
        toast.className = 'son-discount-toast';
        toast.setAttribute('role', 'status');
        toast.innerHTML =
            '<span class="son-discount-toast__icon" aria-hidden="true"></span>' +
            '<span class="son-discount-toast__text"></span>';
        document.body.append(toast);
    }

    return toast;
}

function show(state, text) {
    const node = element();
    node.dataset.state = state;
    node.querySelector('.son-discount-toast__text').textContent = text;
    // reading a layout value commits the hidden state first, so the entrance transitions
    void node.offsetWidth;
    node.classList.add('is-visible');
}

function hide() {
    toast?.classList.remove('is-visible');
}

function setLoading(loading) {
    document.querySelectorAll('.son-personal-price').forEach((node) => {
        node.parentElement?.classList.toggle('son-price-loading', loading);
    });
    // price-roll.js holds changed digits until the pulse is over
    if (!loading) {
        document.dispatchEvent(new CustomEvent('son-price-loading-end'));
    }
}

function reset() {
    phase = 'idle';
    shown = false;
    clearTimers();
    setLoading(false);
}

function reveal() {
    shown = true;
    shownAt = Date.now();
    setLoading(true);
    show('loading', config().recalculating);
}

export function start() {
    if (!config()) {
        return;
    }
    if (phase === 'idle') {
        changed = false;
    }
    phase = 'cart';
    clearTimers();
    later(cancel, MAX_WAIT);
    if (shown) {
        show('loading', config().recalculating);
    } else {
        later(reveal, SHOW_DELAY);
    }
}

// the cart changed: from here the next settled refresh is the one that priced it
function cartChanged() {
    if (!config()) {
        return;
    }
    if (phase === 'idle') {
        start();
    }
    phase = 'prices';
}

export function cancel() {
    reset();
    hide();
}

function finish() {
    if (phase !== 'prices') {
        return;
    }
    const wasShown = shown;
    clearTimers();
    phase = 'done';

    later(() => {
        setLoading(false);
        phase = 'idle';
        shown = false;
        if (!changed) {
            hide();
            return;
        }
        show('applied', config().applied);
        later(hide, APPLIED_SHOWN);
    }, wasShown ? Math.max(0, MIN_LOADING - (Date.now() - shownAt)) : 0);
}

// on document (not body): the bundle loads in <head> before <body> exists
jQuery(document).on('adding_to_cart', start);
jQuery(document).on('ajax_request_not_sent.adding_to_cart', cancel);
jQuery(document).on('added_to_cart removed_from_cart', cartChanged);
// the blocks' events do not bubble past body
document.addEventListener('DOMContentLoaded', () => {
    // a live region is only announced if it is in the page before its text changes
    if (config()) {
        element();
    }
    document.body.addEventListener('wc-blocks_added_to_cart', cartChanged);
    document.body.addEventListener('wc-blocks_removed_from_cart', cartChanged);
});

document.addEventListener('son-price-changed', () => {
    if (phase !== 'idle') {
        changed = true;
    }
});
document.addEventListener('son-personal-price-settled', finish);

// a page left mid-change can come back from the back/forward cache still loading
window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        cancel();
    }
});
